import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { GALLERY } from "@/data/content";
import { useLanguage, ui } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ease = [0.16, 1, 0.3, 1];

// Convert legacy hardcoded gallery items into the new project shape so
// the render pipeline is uniform whether items come from the CMS or defaults.
function fromLegacy(entries) {
    return entries.map((g, i) => ({
        id: g.id,
        title: g.title,
        category: g.category,
        description: g.location,
        images: [{ id: `legacy-${i}`, url: g.src }],
        order: i,
    }));
}

function absUrl(url) {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Gallery() {
    const { locale } = useLanguage();
    const [cat, setCat] = useState(ui(locale, "gallery_all"));
    const [openIdx, setOpenIdx] = useState(null);
    const [openImgIdx, setOpenImgIdx] = useState(0);
    const [projects, setProjects] = useState(null); // null while loading

    useEffect(() => {
        let alive = true;
        axios
            .get(`${API}/gallery?lang=${locale}`)
            .then((r) => {
                if (!alive) return;
                if (Array.isArray(r.data) && r.data.length > 0) {
                    setProjects(r.data);
                } else {
                    setProjects(fromLegacy(GALLERY));
                }
            })
            .catch(() => {
                if (alive) setProjects(fromLegacy(GALLERY));
            });
        return () => { alive = false; };
    }, [locale]);

    useEffect(() => {
        setCat(ui(locale, "gallery_all"));
    }, [locale]);

    const list = projects === null ? fromLegacy(GALLERY) : projects;

    // Build flat list of {project, imageUrl, id} for masonry rendering
    const flat = [];
    for (const p of list) {
        const imgs = p.images && p.images.length > 0 ? p.images : [];
        if (imgs.length === 0) continue;
        for (let i = 0; i < imgs.length; i++) {
            flat.push({
                key: `${p.id}-${i}`,
                project: p,
                imageUrl: absUrl(imgs[i].url),
                imgIndex: i,
            });
        }
    }

    const cats = [ui(locale, "gallery_all"), ...Array.from(new Set(list.map((p) => p.category).filter(Boolean)))];
    const displayedCats = cats;

    const filtered = cat === ui(locale, "gallery_all") ? flat : flat.filter((f) => f.project.category === cat);

    const openProject = (idx, imgIndex = 0) => {
        setOpenIdx(idx);
        setOpenImgIdx(imgIndex);
    };
    const close = () => setOpenIdx(null);
    const current = openIdx !== null ? filtered[openIdx] : null;
    const currentImages = current ? (current.project.images || []) : [];

    const next = () => {
        if (!current) return;
        if (currentImages.length > 1 && openImgIdx < currentImages.length - 1) {
            setOpenImgIdx(openImgIdx + 1);
        } else {
            setOpenIdx((openIdx + 1) % filtered.length);
            setOpenImgIdx(0);
        }
    };
    const prev = () => {
        if (!current) return;
        if (currentImages.length > 1 && openImgIdx > 0) {
            setOpenImgIdx(openImgIdx - 1);
        } else {
            const newIdx = (openIdx - 1 + filtered.length) % filtered.length;
            setOpenIdx(newIdx);
            setOpenImgIdx(0);
        }
    };

    return (
        <section
            id="realisations"
            data-testid="gallery-section"
            className="bg-[#F4F2EE] py-24 md:py-32"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-10 mb-14">
                    <div className="max-w-2xl">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#DC2626]" />
                            <span className="overline">02 · Réalisations</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1E3A5F] leading-[1.05]">
                            Des projets pensés <span className="italic">comme des œuvres.</span>
                        </h2>
                    </div>

                    <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
                        {displayedCats.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCat(c)}
                                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                                className={`text-xs uppercase tracking-[0.2em] px-4 py-3 border transition-colors duration-300 ${
                                    cat === c
                                        ? "bg-[#1E3A5F] text-white border-[#1E3A5F]"
                                        : "bg-transparent text-[#1E3A5F] border-[#1E3A5F]/25 hover:border-[#1E3A5F]"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="masonry" data-testid="gallery-grid">
                    <AnimatePresence mode="popLayout">
                        {filtered.map((f, i) => (
                            <motion.button
                                key={f.key}
                                layout
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.7, delay: (i % 6) * 0.05, ease }}
                                onClick={() => openProject(i, f.imgIndex)}
                                data-testid={`gallery-item-${f.key}`}
                                className="masonry-item img-zoom group relative w-full text-left block"
                            >
                                <img
                                    src={f.imageUrl}
                                    alt={f.project.title}
                                    loading="lazy"
                                    className="w-full h-auto block"
                                />
                                <div className="absolute inset-0 bg-[#1E3A5F]/0 group-hover:bg-[#1E3A5F]/35 transition-colors duration-500 flex items-end p-6">
                                    <div className="opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500 text-white">
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/75">{f.project.category}</div>
                                        <div className="font-serif text-2xl mt-1">{f.project.title}</div>
                                        {f.project.description && (
                                            <div className="text-sm text-white/75 mt-1">{f.project.description}</div>
                                        )}
                                    </div>
                                </div>
                            </motion.button>
                        ))}
                    </AnimatePresence>
                </div>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {openIdx !== null && current && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-[100] bg-[#0d1b2e]/95 backdrop-blur-md flex items-center justify-center p-6"
                        data-testid="gallery-lightbox"
                        onClick={close}
                    >
                        <button onClick={(e) => { e.stopPropagation(); close(); }} data-testid="lightbox-close" className="absolute top-6 right-6 text-white/80 hover:text-white p-3" aria-label="Fermer">
                            <X size={28} strokeWidth={1.4} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" className="absolute left-4 md:left-10 text-white/80 hover:text-white p-3" aria-label="Précédent">
                            <ArrowLeft size={28} strokeWidth={1.4} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" className="absolute right-4 md:right-10 text-white/80 hover:text-white p-3" aria-label="Suivant">
                            <ArrowRight size={28} strokeWidth={1.4} />
                        </button>

                        <motion.div
                            key={`${current.project.id}-${openImgIdx}`}
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5, ease }}
                            onClick={(e) => e.stopPropagation()}
                            className="max-w-5xl w-full"
                        >
                            <img
                                src={absUrl(currentImages[openImgIdx]?.url || current.imageUrl)}
                                alt={current.project.title}
                                className="w-full max-h-[78vh] object-contain"
                            />
                            <div className="mt-6 text-white text-center">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">
                                    {current.project.category}
                                </div>
                                <div className="font-serif text-2xl md:text-3xl mt-2">{current.project.title}</div>
                                {current.project.description && (
                                    <div className="text-sm text-white/65 mt-1">{current.project.description}</div>
                                )}
                                {currentImages.length > 1 && (
                                    <div className="text-xs text-white/50 mt-2">
                                        {openImgIdx + 1} / {currentImages.length}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
